import { useState, useEffect } from "react";
import { toast } from "sonner";
import { CalendarCheck, Bell, BellSlash, ListChecks, CalendarBlank, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSettings } from "@/contexts/SettingsContext";
import { CalendarioSemanalTab } from "@/components/CalendarioSemanalTab";
import { RelatoriosPdfTab } from "@/components/RelatoriosPdfTab";
import { TodoTab } from "@/components/TodoTab";
import Visitas from "@/pages/Visitas";
import { isPushSupported, getPushPermissionState, subscribeToPush, unsubscribeFromPush } from "@/lib/push";
import { PageHeader } from "@/components/PageHeader";

export default function Agenda() {
  const { isReadOnly } = useSettings();
  const [pushState, setPushState] = useState("checking"); // checking | unsupported | default | granted | denied
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) {
      setPushState("unsupported");
      return;
    }
    getPushPermissionState().then(setPushState);
  }, []);

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      if (pushState === "granted") {
        await unsubscribeFromPush();
        setPushState("default");
        toast.success("Notificações desativadas");
      } else {
        await subscribeToPush();
        setPushState("granted");
        toast.success("Notificações ativadas - vai receber um aviso antes de cada tarefa");
      }
    } catch (error) {
      toast.error(error.message || "Erro ao ativar notificações");
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <PageHeader
            icon={CalendarCheck}
            title="Agenda"
            subtitle="Calendário, histórico e relatórios de visitas e tarefas"
            actions={pushState !== "unsupported" && pushState !== "checking" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleTogglePush}
                disabled={pushLoading || pushState === "denied"}
                title={pushState === "denied" ? "Notificações bloqueadas nas definições do navegador" : ""}
                className="shrink-0"
                data-testid="btn-toggle-push-notifications"
              >
                {pushState === "granted" ? <Bell size={16} className="mr-1.5 text-[#009392]" weight="fill" /> : <BellSlash size={16} className="mr-1.5" />}
                <span className="hidden sm:inline">{pushState === "granted" ? "Notificações ativas" : "Ativar notificações"}</span>
                <span className="sm:hidden">{pushState === "granted" ? "Ativas" : "Ativar"}</span>
              </Button>
            )}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs defaultValue="calendario" data-testid="agenda-tabs">
          <div className="flex justify-center">
            <TabsList className="mb-6 flex gap-3 bg-transparent p-0 h-auto">
              <TabsTrigger
                value="calendario"
                data-testid="tab-calendario"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all data-[state=active]:bg-[#009392] data-[state=active]:text-white data-[state=active]:border-[#009392] data-[state=active]:shadow-md data-[state=inactive]:bg-white data-[state=inactive]:text-stone-600 data-[state=inactive]:border-stone-200 hover:border-[#009392]/50"
              >
                <CalendarBlank size={17} /> Calendário
              </TabsTrigger>
              <TabsTrigger
                value="visitas"
                data-testid="tab-visitas"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all data-[state=active]:bg-[#009392] data-[state=active]:text-white data-[state=active]:border-[#009392] data-[state=active]:shadow-md data-[state=inactive]:bg-white data-[state=inactive]:text-stone-600 data-[state=inactive]:border-stone-200 hover:border-[#009392]/50"
              >
                <MapPin size={17} /> Visitas
              </TabsTrigger>
              <TabsTrigger
                value="tarefas"
                data-testid="tab-tarefas"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all data-[state=active]:bg-[#009392] data-[state=active]:text-white data-[state=active]:border-[#009392] data-[state=active]:shadow-md data-[state=inactive]:bg-white data-[state=inactive]:text-stone-600 data-[state=inactive]:border-stone-200 hover:border-[#009392]/50"
              >
                <ListChecks size={17} /> Tarefas
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calendario" className="space-y-10">
            <CalendarioSemanalTab isReadOnly={isReadOnly} />

            <div>
              <RelatoriosPdfTab />
            </div>
          </TabsContent>

          <TabsContent value="visitas">
            <Visitas hideHeader />
          </TabsContent>

          <TabsContent value="tarefas">
            <div>
              <h2 className="text-lg font-bold text-stone-900 mb-3 flex items-center gap-2">
                <ListChecks size={20} className="text-[#009392]" /> Tarefas Pendentes
              </h2>
              <TodoTab />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
